import axios from 'axios';
import { MatchHighlights } from '../types/highlights.types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:27009';

class HighlightsService {
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Fetch all highlights for a specific match
   * @param matchId - The match ID to fetch highlights for
   * @returns MatchHighlights object with all clips
   */
  async getMatchHighlights(matchId: string): Promise<MatchHighlights | null> {
    try {
      const response = await axios.get<MatchHighlights>(
        `${API_URL}/api/Demo/highlights/match/${matchId}`,
        {
          headers: this.getAuthHeader()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching match highlights:', error);
      return null;
    }
  }

  /**
   * Fetch all highlights for a specific player
   * @param steamId - The player's Steam ID
   * @returns MatchHighlights object with all clips (matchId will be generic)
   */
  async getPlayerHighlights(steamId: string): Promise<MatchHighlights | null> {
    try {
      const response = await axios.get<MatchHighlights>(
        `${API_URL}/api/Demo/highlights/player/${steamId}`,
        {
          headers: this.getAuthHeader()
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching player highlights:', error);
      return null;
    }
  }

  /**
   * Download a clip by initiating a browser download
   * @param clipUrl - The CloudFront URL of the clip
   * @param filename - Optional custom filename
   */
  downloadClip(clipUrl: string, filename?: string): void {
    const link = document.createElement('a');
    link.href = clipUrl;
    link.download = filename || `highlight-${Date.now()}.mp4`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Generate a Twitter share URL for a highlight
   * @param clipUrl - The clip URL
   * @param matchId - The match ID
   * @param playerName - Optional player name
   * @param killCount - Number of kills
   * @returns Twitter share URL
   */
  getTwitterShareUrl(
    clipUrl: string,
    matchId: string,
    playerName?: string,
    killCount?: number
  ): string {
    const baseUrl = 'https://twitter.com/intent/tweet';
    const text = playerName && killCount
      ? `Check out this ${killCount}K highlight by ${playerName}!`
      : `Check out this highlight from Northern Championship!`;

    const url = `https://ncseries.gg/match/${matchId}`;

    return `${baseUrl}?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  }

  /**
   * Copy clip URL to clipboard
   * @param clipUrl - The clip URL to copy
   * @returns Promise resolving to success boolean
   */
  async copyToClipboard(clipUrl: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(clipUrl);
      return true;
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = clipUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        return false;
      }
    }
  }
}

export default new HighlightsService();
